import type { IParametersEnumValues, OrderStrategy } from '@talos/kyoko';
import { PreTradeType } from '@talos/kyoko';
import { CHART_PALETTE } from '../../../chartPalette';
import type { PreTradeRequest, PreTradeResponseData } from '../../../redux/preTradeApiTypes';
import { formatCostRiskBps, formatDurationMinutes, parseFiniteNumber } from '../../../utils';

/**
 * A single arrival-aware band to render on the chart.
 * Designed so the eventual "Compare urgencies" feature can pass an array of these.
 */
export interface SnipeSweepBand {
  /** Short identifier shown next to the band (e.g. "U3", scenario name). */
  label: string;
  /** Border/fill colour for the band. */
  color: string;
  /** Snipe bound magnitude (always >= 0). Units match the upstream response. */
  snipeBound: number;
  /** Sweep bound magnitude (always >= 0). Units match the upstream response. */
  sweepBound: number;
  /** Horizontal extent of this band, in minutes from start. */
  durationMinutes: number;
}

/** Y-axis range for the band rectangle, denominated in slippage (Bps). */
export interface SignedBandRange {
  /** Upper y value: sweep magnitude (positive slippage, always >= 0). */
  high: number;
  /** Lower y value: -snipe magnitude (negative slippage, always <= 0). */
  low: number;
}

/**
 * Side-agnostic slippage range for the band rectangle:
 *  - Sweep = positive slippage (worse than arrival) → plotted above the arrival line.
 *  - Snipe = negative slippage (better than arrival) → plotted below the arrival line.
 *
 * The buy/sell distinction is irrelevant because the y-axis is denominated in
 * slippage vs. arrival, not in raw price direction.
 */
export function getSignedBandRange(band: Pick<SnipeSweepBand, 'snipeBound' | 'sweepBound'>): SignedBandRange {
  const snipe = Math.max(0, band.snipeBound);
  const sweep = Math.max(0, band.sweepBound);
  return { high: sweep, low: -snipe };
}

/** Legend line: `U1 -5.00 / +10.00 Bps - 30.00m` (snipe / sweep / duration). */
export function formatBandLegendLabel(band: SnipeSweepBand): string {
  const snipe = Math.max(0, band.snipeBound);
  const sweep = Math.max(0, band.sweepBound);
  return `${band.label} -${formatCostRiskBps(snipe)} / +${formatCostRiskBps(sweep)} Bps - ${formatDurationMinutes(band.durationMinutes)}m`;
}

/** True when the scenario's strategy has `Metadata.PreTradeType === 'Sniper'`; absent/unknown ⇒ false (Base). */
export function isSniperScenario(
  scenario: PreTradeResponseData | undefined,
  strategiesByName: ReadonlyMap<string, OrderStrategy> | undefined
): boolean {
  const strategyName = scenario?.NewOrderSingle?.Strategy ?? scenario?.Strategy;
  if (!strategyName) {
    return false;
  }
  const strategy = strategiesByName?.get(strategyName);
  return strategy?.preTradeType === PreTradeType.Sniper;
}

/** Derives a single band from the scenario. Prefers top-level bounds, falls back to NOS parameters. */
export function buildBandFromScenario(
  scenario: PreTradeResponseData,
  label: string,
  color: string
): SnipeSweepBand | undefined {
  const params = scenario.NewOrderSingle?.Parameters;
  const snipeBound = parseFiniteNumber(scenario.SnipeBound) ?? parseFiniteNumber(params?.SnipeBound) ?? 0;
  const sweepBound = parseFiniteNumber(scenario.SweepBound) ?? parseFiniteNumber(params?.SweepBound) ?? 0;
  const durationMinutes = getDurationMinutes(scenario.StartTime, scenario.EndTime);

  if (snipeBound === 0 && sweepBound === 0) {
    return undefined;
  }
  if (durationMinutes <= 0) {
    return undefined;
  }

  return {
    label,
    color,
    snipeBound,
    sweepBound,
    durationMinutes,
  };
}

/**
 * Converts a scenario's `NewOrderSingle` into a clean `PreTradeRequest['Order']`:
 *   - drops execution-overlay fields (`DecisionStatus`/`OrdStatus`/`CumQty`/`LegSummary`/`MultilegParams`)
 *   - drops submission-tracking fields (`PreTradeSessionID`/`PreTradeRequestID`) for parity with `orderSliceToOrderRequestForPreTrade`
 *   - flattens `Markets` from `PreTradeNOSMarket[]` to `string[]` by using `MarketAccount`
 *     verbatim (already in canonical `Market/Account` form, matching how the OMS form
 *     populates `orderMarketsField.selections` from `MarketAccount` — see SalesOrderSlice)
 *     and falling back to bare `Market` only when no account is present.
 *
 * Used by the Compare flow so it's resilient to the OMS form being cleared while a Sniper
 * scenario is still open — the scenario itself is the source of truth, not the form.
 */
export function toPreTradeOrder(scenario: PreTradeResponseData): PreTradeRequest['Order'] | undefined {
  const nos = scenario.NewOrderSingle;
  if (!nos) {
    return undefined;
  }

  const {
    DecisionStatus: _ds,
    OrdStatus: _os,
    CumQty: _cq,
    LegSummary: _ls,
    MultilegParams: _mp,
    PreTradeSessionID: _ps,
    PreTradeRequestID: _pr,
    Markets,
    ...rest
  } = nos;

  const flattenedMarkets = (Markets ?? []).map(m => m.MarketAccount || m.Market).filter((m): m is string => Boolean(m));

  return { ...rest, Markets: flattenedMarkets } as PreTradeRequest['Order'];
}

/**
 * Maps the parallel pre-trade compare results into renderable bands plus a list of
 * urgency labels that failed (rejected promise, error scenario, missing match, or
 * zero-bound result).
 *
 * The pre-trade compare endpoint returns the full session scenario history, and the
 * fan-out is parallel against a shared `SessionID` — so the trailing entry of any
 * given response is **not** guaranteed to be the scenario created by *that* request
 * (a sibling urgency's processing can finish first and land in the response). We
 * therefore identify each slot's scenario by `NewOrderSingle.Parameters.Urgency`,
 * walking from the end so prior compare runs against the same session don't shadow
 * the freshly-appended scenario.
 *
 * `settled` and `urgencyEnumValues` must be index-aligned (`Promise.allSettled`
 * preserves input order) so we can label rejections without relying on the value.
 */
export function mapPretradeCompareResponse(
  settled: PromiseSettledResult<PreTradeResponseData[]>[],
  urgencyEnumValues: IParametersEnumValues[]
): { bands: SnipeSweepBand[]; failures: string[] } {
  const bands: SnipeSweepBand[] = [];
  const failures: string[] = [];

  settled.forEach((result, index) => {
    const enumValue = urgencyEnumValues[index];
    const label = `U${enumValue.Name}`;
    const color = CHART_PALETTE[index % CHART_PALETTE.length] ?? '#888888';

    if (result.status === 'rejected') {
      failures.push(label);
      return;
    }
    const scenario = findScenarioByUrgency(result.value, enumValue.Index);
    if (!scenario || scenario.Status === 'Error') {
      failures.push(label);
      return;
    }
    const band = buildBandFromScenario(scenario, label, color);
    if (band) {
      bands.push(band);
    } else {
      failures.push(label);
    }
  });

  return { bands, failures };
}

/**
 * Returns the most recent scenario in `scenarios` whose `NewOrderSingle.Parameters.Urgency`
 * matches `targetUrgency`, or `undefined` if none match. Walks in reverse so prior compare
 * runs in the same session don't shadow the freshly-appended scenario. Coerces with
 * `Number(...)` to be defensive about wire variance (the field is documented as `int32`
 * but we've seen quoted strings on this codepath in the past).
 */
function findScenarioByUrgency(
  scenarios: PreTradeResponseData[],
  targetUrgency: number
): PreTradeResponseData | undefined {
  for (let i = scenarios.length - 1; i >= 0; i--) {
    const scenario = scenarios[i];
    const urgency = scenario.NewOrderSingle?.Parameters?.Urgency;
    if (urgency != null && Number(urgency) === targetUrgency) {
      return scenario;
    }
  }
  return undefined;
}

/** Returns duration in minutes between two ISO date strings; 0 on bad inputs. */
export function getDurationMinutes(startISO: string | undefined, endISO: string | undefined): number {
  if (!startISO || !endISO) {
    return 0;
  }
  const start = Date.parse(startISO);
  const end = Date.parse(endISO);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) {
    return 0;
  }
  return (end - start) / 60_000;
}
